import { useAppSelector } from 'src/store';
import { getAppRoutesAvailability } from 'src/routes/state/routesSelector';
import { useLocation } from 'react-router-dom';
import { insightRouteSettings } from 'src/app/ecology-hub/EcologyHub';
import { FeatureType } from 'src/shared/types';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import useProjectFilters from 'src/shared/hooks/useProjectFilters';
import useFeature from 'src/shared/hooks/useFeature';
import useProject from 'src/shared/hooks/useProject';
import useHabitatInsightsAccess from 'src/shared/hooks/useHabitatInsightsAccess';
import { isWithheld } from 'src/shared/utils/productAccess';
import { useIsWithheldFromUser, useIsSetToHidden } from 'src/shared/hooks/useProductLicensing';
import { ProductKey } from 'src/shared/types/engine/products';
import { NestedSidebarMenuSubItem } from './NestedSidebarMenuItem';

const useEcologyHubMenuItem = (): NestedSidebarMenuSubItem | null => {
    const appRoutesAvailability = useAppSelector(getAppRoutesAvailability);
    const navigate = useAppNavigation();
    const { pathname } = useLocation();
    const { currentProjectFilters, currentProjectSelectedHabitatAssayType } = useProjectFilters();
    const { hasFeature } = useFeature();
    const { isHabitatInsightsProject, isStarterProject } = useProject();
    const habitatInsightsAccess = useHabitatInsightsAccess();
    const showsHabitatTrends = isHabitatInsightsProject && !isWithheld(habitatInsightsAccess);
    const isSpeciesInsightsWithheld = useIsWithheldFromUser(ProductKey.SPECIES_INSIGHTS);
    const isHabitatInsightsSetToHidden = useIsSetToHidden(ProductKey.HABITAT_INSIGHTS);
    const isSpeciesInsightsSetToHidden = useIsSetToHidden(ProductKey.SPECIES_INSIGHTS);

    const isEDnaProject = hasFeature({ feature: FeatureType.EDNA });

    const habitatAssays = currentProjectFilters?.habitatAssays;
    const isSingleTimepoint = !!habitatAssays?.find(entry => entry.key === currentProjectSelectedHabitatAssayType?.key)?.singleTimepoint;
    const hasAnyEdnaSource = currentProjectFilters?.habitatAssays?.some(entry => entry.source === 'eDNA');
    const showsSpeciesViews = !isSpeciesInsightsWithheld && Boolean(hasAnyEdnaSource);
    const showsStarterPitch = !isSpeciesInsightsWithheld && isStarterProject;

    if (!appRoutesAvailability.ecologyHub || (!showsSpeciesViews && !showsHabitatTrends && !showsStarterPitch)) {
        return null;
    }

    const isActive = pathname.startsWith('/ecology-hub');

    if (showsStarterPitch) {
        return {
            title: 'Ecology Hub',
            testId: 'ecology-hub-menu-button',
            to: navigate.paths.ecologyHub(),
            isActive,
            isHiddenFromCustomers: isSpeciesInsightsSetToHidden,
        };
    }

    const subItems = [
        ...(showsHabitatTrends
            ? [
                  {
                      title: insightRouteSettings.habitatTrends.name,
                      to: navigate.paths.habitatTrends(),
                      isActive: pathname.includes(insightRouteSettings.habitatTrends.path),
                      isHiddenFromCustomers: isHabitatInsightsSetToHidden,
                  },
              ]
            : []),
        ...(isEDnaProject && showsSpeciesViews
            ? [
                  ...(!isSingleTimepoint
                      ? [
                            {
                                title: insightRouteSettings.speciesTrends.name,
                                to: navigate.paths.speciesTrends(),
                                isActive: pathname.includes(insightRouteSettings.speciesTrends.path),
                            },
                        ]
                      : []),
                  {
                      title: insightRouteSettings.sampleComparison.name,
                      to: navigate.paths.sampleComparison(),
                      isActive: pathname.includes(insightRouteSettings.sampleComparison.path),
                  },
                  {
                      title: insightRouteSettings.statisticalComparison.name,
                      to: navigate.paths.statisticalComparison(),
                      isActive: pathname.includes(insightRouteSettings.statisticalComparison.path),
                  },
                  {
                      title: insightRouteSettings.communityComparison.name,
                      to: navigate.paths.communityComparison(),
                      isActive: pathname.includes(insightRouteSettings.communityComparison.path),
                  },
                  {
                      title: insightRouteSettings.taxonomicComposition.name,
                      to: navigate.paths.taxonomicComposition(),
                      isActive: pathname.includes(insightRouteSettings.taxonomicComposition.path),
                  },
              ].map(view => ({ ...view, isHiddenFromCustomers: isSpeciesInsightsSetToHidden }))
            : []),
    ];

    const namedSubItems = subItems.map(item => ({
        ...item,
        testId: `${item.title.toLowerCase().replace(/\s+/g, '-')}-menu-item`,
    }));

    return {
        title: 'Ecology Hub',
        testId: 'ecology-hub-menu-button',
        subItems: namedSubItems,
    };
};

export default useEcologyHubMenuItem;
